// app/frontend/pages/Community/Post/Show.tsx
import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  Box,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  NavLink,
  ScrollArea,
  Divider,
  ActionIcon,
  Drawer,
  Burger,
  Button,
  Textarea,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconMessageCircle,
  IconPin,
  IconLayoutList,
  IconBell,
  IconVideo,
  IconEdit,
  IconArrowLeft,
  IconCornerDownRight,
} from "@tabler/icons-react";
import { SEO } from "../../../components/common/SEO";
import { L2KText, L2KH5, L2KCaption, L2KBadge } from "../../../components/ui/Typography";
import { PostContent } from "../../../components/community/PostContent";
import { ReactionPicker } from "../../../components/community/ReactionPicker";

interface User {
  id: number;
  name: string;
  username: string;
  avatar_url: string | null;
}

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  admin: boolean;
}

interface ReactionsSummary {
  [emoji: string]: number;
}

interface Comment {
  id: number;
  body: string;
  hidden: boolean;
  reactions_count: number;
  replies_count: number;
  created_at: string;
  user: User;
  user_reaction?: string | null;
  reactions_summary?: ReactionsSummary;
  replies?: Comment[];
}

interface Post {
  id: number;
  title: string;
  body: string;
  pinned: boolean;
  hidden: boolean;
  comments_count: number;
  reactions_count: number;
  last_activity_at: string;
  created_at: string;
  user: User;
  reactions_summary?: ReactionsSummary;
  user_reaction?: string | null;
}

interface Space {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  emoji: string;
  posts_count: number;
  category?: string | null;
}

interface Props {
  space: Space;
  post: Post;
  comments: Comment[];
  can_edit: boolean;
  can_admin: boolean;
  spaces: Space[];
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

interface CommentItemProps {
  comment: Comment;
  spaceSlug: string;
  postId: number;
  onReplyAdded: () => void;
  isDesktop: boolean;
}

function CommentItem({ comment, spaceSlug, postId, onReplyAdded, isDesktop }: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const handleSubmitReply = async () => {
    if (!replyBody.trim()) return;

    setIsSubmittingReply(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(`/community/spaces/${spaceSlug}/posts/${postId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          comment: {
            body: replyBody,
            parent_id: comment.id,
          },
        }),
      });

      if (response.ok) {
        setReplyBody("");
        setShowReplyForm(false);
        onReplyAdded();
      }
    } catch (error) {
      console.error("Failed to submit reply:", error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  return (
    <Card
      padding={isDesktop ? "md" : "sm"}
      radius={isDesktop ? "md" : "sm"}
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.02)",
        border: "1px solid rgba(255, 255, 255, 0.06)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Stack gap={isDesktop ? "xs" : 6}>
        <Group gap={isDesktop ? "sm" : "xs"} wrap="nowrap">
          <Avatar
            src={comment.user.avatar_url}
            alt={comment.user.name}
            radius="xl"
            size={isDesktop ? "sm" : "xs"}
            color="orange"
            style={{ flexShrink: 0 }}
          >
            {comment.user.name.charAt(0)}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <L2KText size="xs" style={{ fontWeight: 500, fontSize: isDesktop ? 13 : 12 }}>
              {comment.user.name}
            </L2KText>
            <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 11 : 10 }}>
              {formatRelativeTime(comment.created_at)}
            </L2KCaption>
          </Box>
        </Group>

        <L2KText size="xs" variant="secondary" style={{ whiteSpace: "pre-wrap", fontSize: isDesktop ? 13 : 12, lineHeight: 1.5 }}>
          {comment.body}
        </L2KText>

        {/* Comment Actions: Reactions + Reply */}
        <Group gap={isDesktop ? "md" : "sm"} mt={2}>
          <ReactionPicker
            reactableType="Comment"
            reactableId={comment.id}
            userReaction={comment.user_reaction || null}
            reactionsSummary={comment.reactions_summary || {}}
            reactionsCount={comment.reactions_count}
            size="xs"
          />
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            leftSection={<IconCornerDownRight size={11} />}
            onClick={() => setShowReplyForm(!showReplyForm)}
            style={{ color: "#737373", padding: isDesktop ? undefined : "0 6px" }}
          >
            Reply
          </Button>
        </Group>

        {/* Reply Form */}
        {showReplyForm && (
          <Box mt="xs" ml={isDesktop ? "md" : "sm"} style={{ borderLeft: "2px solid #3a3a3a", paddingLeft: isDesktop ? "1rem" : "0.5rem" }}>
            <Stack gap="xs">
              <Textarea
                placeholder="Write a reply..."
                value={replyBody}
                onChange={(e) => setReplyBody(e.currentTarget.value)}
                minRows={2}
                styles={{
                  input: {
                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    color: "#e5e5e5",
                    fontSize: isDesktop ? "13px" : "12px",
                    "&:focus": {
                      borderColor: "rgba(255, 152, 0, 0.5)",
                    },
                  },
                }}
              />
              <Group gap="xs" justify="flex-end">
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  onClick={() => {
                    setShowReplyForm(false);
                    setReplyBody("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  loading={isSubmittingReply}
                  disabled={!replyBody.trim()}
                  onClick={handleSubmitReply}
                  styles={{
                    root: {
                      background: !replyBody.trim() ? "rgba(255, 255, 255, 0.08)" : "linear-gradient(135deg, #f97316, #ea580c)",
                      border: !replyBody.trim() ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                      color: !replyBody.trim() ? "#555" : "#fff",
                      "&:disabled": {
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        color: "#555",
                      },
                    },
                  }}
                >
                  Reply
                </Button>
              </Group>
            </Stack>
          </Box>
        )}

        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <Stack gap={isDesktop ? "sm" : "xs"} ml={isDesktop ? "md" : "sm"} mt={isDesktop ? "sm" : "xs"} style={{ borderLeft: "2px solid #3a3a3a", paddingLeft: isDesktop ? "1rem" : "0.5rem" }}>
            {comment.replies.map((reply) => (
              <Box key={reply.id}>
                <Group gap="xs" wrap="nowrap">
                  <Avatar
                    src={reply.user.avatar_url}
                    alt={reply.user.name}
                    radius="xl"
                    size="xs"
                    color="orange"
                    style={{ flexShrink: 0 }}
                  >
                    {reply.user.name.charAt(0)}
                  </Avatar>
                  <L2KCaption style={{ fontWeight: 500, fontSize: isDesktop ? 12 : 11 }}>
                    {reply.user.name}
                  </L2KCaption>
                  <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 11 : 10 }}>
                    {formatRelativeTime(reply.created_at)}
                  </L2KCaption>
                </Group>
                <L2KText size="xs" variant="secondary" style={{ whiteSpace: "pre-wrap", marginTop: 4, marginLeft: isDesktop ? 28 : 22, fontSize: isDesktop ? 13 : 12 }}>
                  {reply.body}
                </L2KText>
                {/* Reply Reactions */}
                <Box ml={isDesktop ? 28 : 22} mt={4}>
                  <ReactionPicker
                    reactableType="Comment"
                    reactableId={reply.id}
                    userReaction={reply.user_reaction || null}
                    reactionsSummary={reply.reactions_summary || {}}
                    reactionsCount={reply.reactions_count}
                    size="xs"
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

interface SidebarContentProps {
  spaces: Space[];
  currentSpaceSlug: string;
  currentUser: CurrentUser;
  onNavClick?: () => void;
}

function SidebarContent({ spaces, currentSpaceSlug, currentUser, onNavClick }: SidebarContentProps) {
  const spacesByCategory: Record<string, Space[]> = {};
  spaces.forEach((space) => {
    const category = space.category || "Other";
    if (!spacesByCategory[category]) {
      spacesByCategory[category] = [];
    }
    spacesByCategory[category].push(space);
  });

  const categoryOrder = ["Welcome", "Community", "Resources", "Other"];

  return (
    <Box
      style={{
        backgroundColor: "#1a1a1a",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box px="md" py="lg" style={{ borderBottom: "1px solid #2a2a2a" }}>
        <Link href="/" style={{ textDecoration: "none" }} onClick={onNavClick}>
          <Text
            size="xl"
            fw={700}
            style={{
              background: "linear-gradient(135deg, #f97316, #ea580c)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Learntokiz
          </Text>
        </Link>
      </Box>

      <ScrollArea flex={1} px="sm" py="md">
        <Stack gap={4}>
          <NavLink
            component={Link}
            href="/videos"
            label="Learn"
            leftSection={<IconVideo size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
              },
              label: { color: "#e5e5e5" },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          <NavLink
            component={Link}
            href="/community"
            label="Feed"
            leftSection={<IconLayoutList size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
              },
              label: { color: "#e5e5e5" },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {categoryOrder.map((category) => {
            const categorySpaces = spacesByCategory[category];
            if (!categorySpaces || categorySpaces.length === 0) return null;

            return (
              <Box key={category} mb="md">
                <Text size="xs" fw={600} c="#737373" tt="uppercase" px="sm" mb="xs">
                  {category}
                </Text>
                {categorySpaces.map((space) => (
                  <NavLink
                    key={space.id}
                    component={Link}
                    href={`/community/spaces/${space.slug}`}
                    label={space.name}
                    leftSection={<Text size="sm">{space.emoji}</Text>}
                    active={space.slug === currentSpaceSlug}
                    onClick={onNavClick}
                    styles={{
                      root: {
                        borderRadius: 8,
                        color: "#e5e5e5",
                        "&[data-active]": {
                          backgroundColor: "rgba(249, 115, 22, 0.15)",
                          color: "#f97316",
                        },
                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
                      },
                      label: { color: "inherit" },
                    }}
                  />
                ))}
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>

      <Box px="md" py="md" style={{ borderTop: "1px solid #2a2a2a" }}>
        <Group>
          <Avatar src={currentUser.avatar_url} alt={currentUser.name} radius="xl" size="sm" color="orange">
            {currentUser.name.charAt(0)}
          </Avatar>
          <Box flex={1} style={{ minWidth: 0 }}>
            <L2KText size="xs" style={{ fontWeight: 500 }}>
              {currentUser.name}
            </L2KText>
          </Box>
        </Group>
      </Box>
    </Box>
  );
}

export default function PostShow({ space, post, comments, can_edit, can_admin, spaces }: Props) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const { current_user } = usePage<{ current_user: CurrentUser }>().props;
  const [newComment, setNewComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    setIsSubmitting(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(`/community/spaces/${space.slug}/posts/${post.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({ comment: { body: newComment } }),
      });

      if (response.ok) {
        setNewComment("");
        window.location.reload();
      } else {
        notifications.show({
          color: "red",
          title: "Error",
          message: "Failed to post comment",
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Failed to post comment",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
      }}
    >
      <SEO
        title={`${post.title} - ${space.name} - Learntokiz`}
        description={
          post.body.slice(0, 160).replace(/[#*_`\[\]]/g, "").trim() ||
          `${post.title} in ${space.name} on Learntokiz`
        }
        url={`https://learntokiz.io/community/${space.slug}/posts/${post.id}`}
        type="article"
      />

      {/* Mobile Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={280}
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0, height: "100%" },
          content: { backgroundColor: "#1a1a1a" },
        }}
      >
        <SidebarContent
          spaces={spaces}
          currentSpaceSlug={space.slug}
          currentUser={current_user}
          onNavClick={closeDrawer}
        />
      </Drawer>

      {/* Desktop Sidebar */}
      {isDesktop && (
        <Box
          style={{
            width: 280,
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            borderRight: "1px solid #2a2a2a",
            zIndex: 100,
          }}
        >
          <SidebarContent spaces={spaces} currentSpaceSlug={space.slug} currentUser={current_user} />
        </Box>
      )}

      {/* Mobile Header */}
      {!isDesktop && (
        <Box
          px="md"
          py="sm"
          style={{
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid #2a2a2a",
            position: "sticky",
            top: 0,
            zIndex: 50,
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Burger opened={drawerOpened} onClick={openDrawer} size="sm" color="#e5e5e5" />
              <Link href="/" style={{ textDecoration: "none" }}>
                <Text
                  size="lg"
                  fw={700}
                  style={{
                    background: "linear-gradient(135deg, #f97316, #ea580c)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Learntokiz
                </Text>
              </Link>
            </Group>
            <ActionIcon variant="subtle" color="gray" size="lg" style={{ color: "#a3a3a3" }}>
              <IconBell size={20} />
            </ActionIcon>
          </Group>
        </Box>
      )}

      {/* Main Content */}
      <Box
        style={{
          marginLeft: isDesktop ? 280 : 0,
          padding: isDesktop ? "2rem" : "0.75rem",
          maxWidth: isDesktop ? 800 : "100%",
        }}
      >
        <Stack gap={isDesktop ? "lg" : "sm"}>
          {/* Breadcrumb */}
          <Link href={`/community/spaces/${space.slug}`} style={{ textDecoration: "none" }}>
            <Group gap="xs">
              <IconArrowLeft size={isDesktop ? 16 : 14} color="#B8B8B8" />
              <L2KCaption variant="tertiary">
                {space.emoji} {space.name}
              </L2KCaption>
            </Group>
          </Link>

          {/* Post Card */}
          <Card
            padding={isDesktop ? "xl" : "sm"}
            radius={isDesktop ? "md" : "sm"}
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Stack gap={isDesktop ? "sm" : "xs"}>
              {/* Author */}
              <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                <Group gap={isDesktop ? "sm" : "xs"} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={post.user.avatar_url}
                    alt={post.user.name}
                    radius="xl"
                    size={isDesktop ? "md" : "sm"}
                    color="orange"
                    style={{ flexShrink: 0 }}
                  >
                    {post.user.name.charAt(0)}
                  </Avatar>
                  <Box style={{ minWidth: 0 }}>
                    <L2KText size="xs" style={{ fontWeight: 500 }}>
                      {post.user.name}
                    </L2KText>
                    <L2KCaption variant="tertiary" style={{ fontSize: isDesktop ? 12 : 11 }}>
                      {formatRelativeTime(post.created_at)}
                    </L2KCaption>
                  </Box>
                </Group>
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  {post.pinned && (
                    <L2KBadge variant="subtle" size="xs" leftSection={<IconPin size={10} />}>
                      Pinned
                    </L2KBadge>
                  )}
                  {can_edit && (
                    <Link href={`/community/spaces/${space.slug}/posts/${post.id}/edit`}>
                      <Button variant="subtle" size="xs" leftSection={<IconEdit size={12} />} color="gray" px={isDesktop ? "xs" : 4}>
                        {isDesktop ? "Edit" : ""}
                      </Button>
                    </Link>
                  )}
                </Group>
              </Group>

              {/* Title */}
              <L2KText size={isDesktop ? "md" : "sm"} style={{ fontWeight: 600 }}>{post.title}</L2KText>

              {/* Body with Link Previews */}
              <PostContent body={post.body} size={isDesktop ? "sm" : "xs"} />

              <Divider color="#2a2a2a" />

              {/* Stats & Reactions */}
              <Group gap="md" justify="space-between">
                <Group gap="md">
                  <Group gap={4}>
                    <IconMessageCircle size={14} color="#B8B8B8" />
                    <L2KCaption variant="tertiary">
                      {post.comments_count} {post.comments_count === 1 ? "comment" : "comments"}
                    </L2KCaption>
                  </Group>
                </Group>
                <ReactionPicker
                  reactableType="Post"
                  reactableId={post.id}
                  userReaction={post.user_reaction || null}
                  reactionsSummary={post.reactions_summary || {}}
                  reactionsCount={post.reactions_count}
                />
              </Group>
            </Stack>
          </Card>

          {/* Comments Section */}
          <Box>
            <Group gap="xs" mb={isDesktop ? "md" : "sm"}>
              <L2KText size={isDesktop ? "sm" : "xs"} style={{ fontWeight: 600 }}>
                Comments
              </L2KText>
              <L2KBadge variant="subtle" size="xs">
                {comments.length}
              </L2KBadge>
            </Group>

            {/* New Comment Form */}
            <Card
              padding={isDesktop ? "md" : "sm"}
              radius={isDesktop ? "md" : "sm"}
              mb={isDesktop ? "lg" : "sm"}
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Stack gap={isDesktop ? "sm" : "xs"}>
                <Textarea
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.currentTarget.value)}
                  minRows={isDesktop ? 3 : 2}
                  styles={{
                    input: {
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#e5e5e5",
                      fontSize: isDesktop ? "14px" : "13px",
                      "&:focus": {
                        borderColor: "rgba(255, 152, 0, 0.5)",
                      },
                    },
                  }}
                />
                <Group justify="flex-end">
                  <Button
                    onClick={handleSubmitComment}
                    loading={isSubmitting}
                    disabled={!newComment.trim()}
                    size={isDesktop ? "sm" : "xs"}
                    styles={{
                      root: {
                        background: !newComment.trim() ? "rgba(255, 255, 255, 0.08)" : "linear-gradient(135deg, #f97316, #ea580c)",
                        border: !newComment.trim() ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                        color: !newComment.trim() ? "#555" : "#fff",
                        "&:disabled": {
                          background: "rgba(255, 255, 255, 0.08)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          color: "#555",
                        },
                      },
                    }}
                  >
                    Post Comment
                  </Button>
                </Group>
              </Stack>
            </Card>

            {/* Comments List */}
            <Stack gap="sm">
              {comments.length === 0 ? (
                <L2KText size="xs" variant="tertiary" style={{ textAlign: "center" }}>
                  No comments yet. Be the first to comment!
                </L2KText>
              ) : (
                comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    spaceSlug={space.slug}
                    postId={post.id}
                    onReplyAdded={() => window.location.reload()}
                    isDesktop={isDesktop ?? false}
                  />
                ))
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
